import axios, { AxiosInstance } from 'axios';
import { API_CONFIG, API_ENDPOINTS } from '@/shared/config/api';
import { cacheService } from './cacheService';
import { CACHE_KEYS, CACHE_CONFIG } from '@/shared/config/cache';
import logger from '@/shared/utils/logger';
import type { ApiResponse, User, GuideWithExercises, AppSettings, AppSetting } from '@/shared/types';
import type { UserProfile } from '@/profile/types';
import type { Submission, UserSubmissionsPayload } from '@/submissions/types';
import type { SubmissionResponse, SubmissionForm, ExerciseRankingsData } from '@/code/types';
import type { UserStatus, CSVUploadResult, AdminGuide, AdminUser, UserDetailData, UserTermsResponse, PaginatedAdminUsersResponse, StudentProgressMetric, AvgResolutionTimeMetric, ExerciseAttemptsMetric, ActiveStudentsMetric, ExerciseErrorRateMetric, StudentAtRiskMetric, ProgressDistributionMetric, WeeklyActivityMetric, ExerciseCompletionMatrixMetric } from '@/admin/types';

const AUTH_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_CONFIG.baseURL,
      timeout: API_CONFIG.timeout,
      headers: API_CONFIG.headers,
    });

    // Request interceptor para agregar token de autenticación
    this.api.interceptors.request.use(
      (config) => {
        const url = config.url ?? '';
        const isAuthLogin = url.includes(API_ENDPOINTS.auth.login);

        if (!isAuthLogin) {
          config.headers = config.headers ?? {};
          const authToken = localStorage.getItem(AUTH_TOKEN_KEY);
          const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

          if (authToken) {
            config.headers['X-Auth-Token'] = authToken;
          }

          if (refreshToken) {
            config.headers['X-Refresh-Token'] = refreshToken;
          }
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor para manejar errores globalmente
    this.api.interceptors.response.use(
      (response) => {
        const newAuthToken = response.headers['x-auth-token'];
        const newRefreshToken = response.headers['x-refresh-token'];

        if (newAuthToken && newRefreshToken) {
          this.setAuthTokens(newAuthToken, newRefreshToken);
        }

        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem(AUTH_TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          localStorage.removeItem('user_data');
          cacheService.invalidateAll();
          logger.warn('Session expired, redirecting to login');
          window.dispatchEvent(new Event('auth:session-expired'));
        }
        return Promise.reject(error);
      }
    );
  }

  // Health check
  async healthCheck(): Promise<ApiResponse> {
    const response = await this.api.get(API_ENDPOINTS.health);
    return response.data;
  }

  // User endpoints
  async getCurrentUser(): Promise<ApiResponse<User>> {
    const response = await this.api.get(API_ENDPOINTS.users.me);
    return response.data;
  }

  async getUserProfile(): Promise<ApiResponse<UserProfile>> {
    const response = await this.api.get(API_ENDPOINTS.users.profile);
    return response.data;
  }

  async getAllUsers(): Promise<ApiResponse<User[]>> {
    const response = await this.api.get(API_ENDPOINTS.users.all);
    return response.data;
  }

  async getUserById(id: string): Promise<ApiResponse<User>> {
    const response = await this.api.get(API_ENDPOINTS.users.byId(id));
    return response.data;
  }

  async deleteUser(id: string): Promise<ApiResponse> {
    const response = await this.api.delete(API_ENDPOINTS.users.delete(id));
    return response.data;
  }

  async updateProfile(data: { fullName?: string; receiveAlerts?: boolean }): Promise<ApiResponse<UserProfile>> {
    const response = await this.api.patch(API_ENDPOINTS.users.updateMe, data);
    return response.data;
  }

  // Submission endpoints
  async submitSolution(formData: SubmissionForm): Promise<ApiResponse<SubmissionResponse>> {
    const response = await this.api.post(API_ENDPOINTS.submissions.submit, formData);

    // Invalidar caché relacionado después de submit exitoso
    if (response.data.success) {
      this.invalidateUserCache();
    }

    return response.data;
  }

  /**
   * Envíos y progreso del alumno, acotados a un cuatrimestre.
   * Sin `term`, el backend resuelve el default (D34) y lo informa en la respuesta.
   */
  async getMySubmissions(term?: string): Promise<ApiResponse<UserSubmissionsPayload>> {
    const response = await this.api.get(API_ENDPOINTS.submissions.my, {
      params: term ? { term } : undefined,
    });
    return response.data;
  }

  async getSubmissionById(id: string): Promise<ApiResponse<Submission>> {
    const response = await this.api.get(API_ENDPOINTS.submissions.byId(id));
    return response.data;
  }

  async getSubmissionStatus(id: string): Promise<ApiResponse<SubmissionResponse>> {
    const response = await this.api.get(API_ENDPOINTS.submissions.status(id));
    return response.data;
  }

  async getExerciseRankings(guideNumber: number, exerciseNumber: number): Promise<ApiResponse<ExerciseRankingsData>> {
    const response = await this.api.get(API_ENDPOINTS.submissions.rankings(guideNumber, exerciseNumber));
    logger.log(response.data);
    return response.data;
  }

  async getAvailableExercises(): Promise<ApiResponse<GuideWithExercises[]>> {
    const response = await this.api.get(API_ENDPOINTS.submissions.availableExercises);
    return response.data;
  }

  async getAvailableExercisesConditional(
    etag?: string | null
  ): Promise<
    | { notModified: true }
    | { notModified: false; data: GuideWithExercises[]; etag: string | null }
  > {
    const headers: Record<string, string> = {};
    if (etag) headers['If-None-Match'] = etag;

    const response = await this.api.get(
      API_ENDPOINTS.submissions.availableExercises,
      {
        headers,
        validateStatus: (s) => (s >= 200 && s < 300) || s === 304,
      }
    );

    if (response.status === 304) return { notModified: true };

    return {
      notModified: false,
      data: response.data.data as GuideWithExercises[],
      etag: (response.headers['etag'] as string | undefined) ?? null,
    };
  }

  // Admin endpoints
  async uploadCSV(file: File): Promise<ApiResponse<CSVUploadResult>> {
    const formData = new FormData();
    formData.append('csv', file);

    const response = await this.api.post(API_ENDPOINTS.admin.uploadCSV, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  async getUserStatus(): Promise<ApiResponse<UserStatus>> {
    const response = await this.api.get(API_ENDPOINTS.admin.userStatus);
    return response.data;
  }

  async getAdminUsers(params?: { search?: string; page?: number; limit?: number; role?: string; enabled?: boolean; term?: string }): Promise<ApiResponse<PaginatedAdminUsersResponse>> {
    const response = await this.api.get(API_ENDPOINTS.admin.users, { params });
    return response.data;
  }

  /** Cuatrimestres existentes + el actual (S1). Sin caché: cambia al subir un CSV. */
  async getUserTerms(): Promise<ApiResponse<UserTermsResponse>> {
    const response = await this.api.get(API_ENDPOINTS.admin.userTerms);
    return response.data;
  }

  async updateUserTerm(userId: string, term: string | null): Promise<ApiResponse<AdminUser>> {
    const response = await this.api.patch(API_ENDPOINTS.admin.userTerm(userId), { term });
    return response.data;
  }

  async updateUserEnabled(userId: string, enabled: boolean): Promise<ApiResponse<AdminUser>> {
    const response = await this.api.patch(API_ENDPOINTS.admin.userEnabled(userId), { enabled });
    return response.data;
  }

  async updateUserRoles(userId: string, roles: string[]): Promise<ApiResponse<{ userId: string; roles: string[] }>> {
    const response = await this.api.put(API_ENDPOINTS.admin.userRoles(userId), { roles });
    return response.data;
  }

  async getAdminUserDetails(userId: string, term?: string): Promise<ApiResponse<UserDetailData>> {
    const response = await this.api.get(API_ENDPOINTS.admin.userDetails(userId), {
      params: term ? { term } : undefined,
    });
    return response.data;
  }

  async getAdminUserSubmissions(
    userId: string,
    guideNumber: number,
    exerciseNumber: number,
    term?: string
  ): Promise<ApiResponse<Submission[]>> {
    const response = await this.api.get(
      API_ENDPOINTS.admin.userSubmissions(userId, guideNumber, exerciseNumber, term)
    );
    return response.data;
  }

  // Admin guide/exercise endpoints
  async getAdminGuides(): Promise<ApiResponse<AdminGuide[]>> {
    const response = await this.api.get(API_ENDPOINTS.admin.guide());
    return response.data;
  }

  async createGuide(data: { guideNumber: number; enabled?: boolean; deadline?: string | null }): Promise<ApiResponse<AdminGuide>> {
    const response = await this.api.post(API_ENDPOINTS.admin.guide(), data);
    return response.data;
  }

  async updateGuide(n: number, data: { enabled?: boolean; deadline?: string | null }): Promise<ApiResponse<AdminGuide>> {
    const response = await this.api.patch(API_ENDPOINTS.admin.guide(n), data);
    return response.data;
  }

  async deleteGuide(n: number): Promise<ApiResponse> {
    const response = await this.api.delete(API_ENDPOINTS.admin.guide(n));
    return response.data;
  }

  async createExercise(g: number, data: { exerciseNumber: number; enabled?: boolean; functionSignature?: string | null }): Promise<ApiResponse> {
    const response = await this.api.post(API_ENDPOINTS.admin.exercise(g), data);
    return response.data;
  }

  async updateExercise(g: number, e: number, data: { enabled?: boolean; functionSignature?: string | null }): Promise<ApiResponse> {
    const response = await this.api.patch(API_ENDPOINTS.admin.exercise(g, e), data);
    return response.data;
  }

  async deleteExercise(g: number, e: number): Promise<ApiResponse> {
    const response = await this.api.delete(API_ENDPOINTS.admin.exercise(g, e));
    return response.data;
  }

  async uploadExerciseTestFile(g: number, e: number, file: File): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append('testFile', file);
    const response = await this.api.post(API_ENDPOINTS.admin.exerciseTestFile(g, e), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteExerciseTestFile(g: number, e: number): Promise<ApiResponse> {
    const response = await this.api.delete(API_ENDPOINTS.admin.exerciseTestFile(g, e));
    return response.data;
  }

  async downloadExerciseTestFile(g: number, e: number): Promise<void> {
    const response = await this.api.get(API_ENDPOINTS.admin.exerciseTestFile(g, e), {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `exercise-${e}.c`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ── Metrics endpoints (5 min cache) ────────────────────────────────────────

  private async getCachedMetric<T>(cacheKey: string, url: string): Promise<ApiResponse<T>> {
    const cached = cacheService.get<T>(cacheKey);
    if (cached !== null) {
      return { success: true, data: cached };
    }
    const response = await this.api.get<ApiResponse<T>>(url);
    if (response.data.success && response.data.data !== undefined) {
      cacheService.set(cacheKey, response.data.data, CACHE_CONFIG.metrics);
    }
    return response.data;
  }

  /** Los 9 endpoints de métricas exigen `term` (S2): sin él el backend responde 400. */
  private withTerm(url: string, term: string): string {
    return `${url}?term=${encodeURIComponent(term)}`;
  }

  async getMetricsProgress(term: string): Promise<ApiResponse<StudentProgressMetric[]>> {
    return this.getCachedMetric<StudentProgressMetric[]>(
      CACHE_KEYS.metricsProgress(term),
      this.withTerm(API_ENDPOINTS.admin.metrics.progress, term),
    );
  }

  async getMetricsResolutionTime(term: string): Promise<ApiResponse<AvgResolutionTimeMetric>> {
    return this.getCachedMetric<AvgResolutionTimeMetric>(
      CACHE_KEYS.metricsResolutionTime(term),
      this.withTerm(API_ENDPOINTS.admin.metrics.resolutionTime, term),
    );
  }

  async getMetricsAttempts(term: string): Promise<ApiResponse<ExerciseAttemptsMetric[]>> {
    return this.getCachedMetric<ExerciseAttemptsMetric[]>(
      CACHE_KEYS.metricsAttempts(term),
      this.withTerm(API_ENDPOINTS.admin.metrics.attempts, term),
    );
  }

  async getMetricsActiveStudents(term: string): Promise<ApiResponse<ActiveStudentsMetric>> {
    return this.getCachedMetric<ActiveStudentsMetric>(
      CACHE_KEYS.metricsActiveStudents(term),
      this.withTerm(API_ENDPOINTS.admin.metrics.activeStudents, term),
    );
  }

  async getMetricsErrorRate(term: string): Promise<ApiResponse<ExerciseErrorRateMetric[]>> {
    return this.getCachedMetric<ExerciseErrorRateMetric[]>(
      CACHE_KEYS.metricsErrorRate(term),
      this.withTerm(API_ENDPOINTS.admin.metrics.errorRate, term),
    );
  }

  async getMetricsAtRisk(term: string): Promise<ApiResponse<StudentAtRiskMetric[]>> {
    return this.getCachedMetric<StudentAtRiskMetric[]>(
      CACHE_KEYS.metricsAtRisk(term),
      this.withTerm(API_ENDPOINTS.admin.metrics.atRisk, term),
    );
  }

  async getMetricsProgressDistribution(term: string): Promise<ApiResponse<ProgressDistributionMetric[]>> {
    return this.getCachedMetric<ProgressDistributionMetric[]>(
      CACHE_KEYS.metricsProgressDistribution(term),
      this.withTerm(API_ENDPOINTS.admin.metrics.progressDistribution, term),
    );
  }

  async getMetricsWeeklyActivity(term: string): Promise<ApiResponse<WeeklyActivityMetric[]>> {
    return this.getCachedMetric<WeeklyActivityMetric[]>(
      CACHE_KEYS.metricsWeeklyActivity(term),
      this.withTerm(API_ENDPOINTS.admin.metrics.weeklyActivity, term),
    );
  }

  async getMetricsCompletionMatrix(term: string): Promise<ApiResponse<ExerciseCompletionMatrixMetric[]>> {
    return this.getCachedMetric<ExerciseCompletionMatrixMetric[]>(
      CACHE_KEYS.metricsCompletionMatrix(term),
      this.withTerm(API_ENDPOINTS.admin.metrics.completionMatrix, term),
    );
  }

  async rejectSubmission(payload: {
    userId: string;
    guideNumber: number;
    exerciseNumber: number;
    createdAt: string;
    rejectedReason: string;
    notifyUser: boolean;
  }): Promise<ApiResponse<null>> {
    const response = await this.api.patch(API_ENDPOINTS.admin.rejectSubmission, payload);
    return response.data;
  }

  // Test endpoints
  async testSendDeadlineAlert(email: string): Promise<ApiResponse<{ sentTo: string; guideNumber: number; skipped?: boolean }>> {
    const response = await this.api.post(API_ENDPOINTS.admin.testSendDeadlineAlert, { email });
    return response.data;
  }

  // Settings endpoints
  async getPublicSettings(): Promise<ApiResponse<AppSettings>> {
    const response = await this.api.get(API_ENDPOINTS.settings.public);
    return response.data;
  }

  async getAllSettings(): Promise<ApiResponse<AppSetting[]>> {
    const response = await this.api.get(API_ENDPOINTS.settings.adminAll);
    return response.data;
  }

  async updateSetting(key: string, value: string | null): Promise<ApiResponse<AppSetting>> {
    const response = await this.api.patch(API_ENDPOINTS.settings.adminUpdate(key), { value });
    return response.data;
  }

  // Utility method to set auth tokens
  setAuthTokens(authToken: string, refreshToken: string): void {
    localStorage.setItem(AUTH_TOKEN_KEY, authToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }

  // Utility method to clear auth tokens
  clearAuthTokens(): void {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  // Cache management methods
  invalidateUserCache(): void {
    // Invalidar caché de submissions y profile después de submit.
    // Las claves de submissions están particionadas por cuatrimestre, así que se
    // invalidan todas por prefijo: un envío cambia el progreso del term en curso.
    cacheService.invalidateByPrefix(CACHE_KEYS.mySubmissionsPrefix);
    cacheService.invalidate(CACHE_KEYS.userProfile());
  }

  invalidateAllCache(): void {
    cacheService.invalidateAll();
  }

  invalidateCacheByKey(key: string): void {
    cacheService.invalidate(key);
  }
}

export const apiService = new ApiService();
export default apiService;
